import type { ReactNode } from "react";
import type { NavItem, Workspace } from "./types";
import { NavIcons } from "./icons";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";

/**
 * CrmRail
 * ─────────────────────────────────────────────────────────────
 * The slim 56px icon rail used by the customer-facing CRM.
 *
 * - WorkspaceSwitcher pinned to the top (slim variant). For
 *   customer users with one workspace this is just a brand mark;
 *   for internal staff impersonating, it pops the switcher.
 * - Primary nav items in the middle (hover reveals a tooltip).
 * - Settings sits at the bottom on its own — Help & Docs and
 *   Feedback live in the topbar user menu, not here.
 *
 * Presentational. The caller wires `activeId`, the items list,
 * and onNavigate.
 */

export interface CrmRailProps {
  workspaces: Workspace[];
  activeWorkspaceId: string;
  /** True if currently impersonating (re-tints the rail + switcher). */
  isImpersonating?: boolean;
  /** Top-of-rail primary destinations. */
  items: NavItem[];
  /** Pinned at the bottom (just Settings by default). */
  footerItems?: NavItem[];
  activeId?: string;
  onNavigate?: (item: NavItem) => void;
  onSwitchWorkspace?: (workspaceId: string) => void;
}

export function CrmRail({
  workspaces,
  activeWorkspaceId,
  isImpersonating,
  items,
  footerItems = [],
  activeId,
  onNavigate,
  onSwitchWorkspace,
}: CrmRailProps) {
  return (
    <aside
      className={"rr-rail rr-rail--crm" + (isImpersonating ? " is-impersonating" : "")}
      aria-label="Primary"
    >
      <div className="rr-rail-head">
        <WorkspaceSwitcher
          workspaces={workspaces}
          activeId={activeWorkspaceId}
          variant="rail-slim"
          isImpersonating={isImpersonating}
          onSelect={onSwitchWorkspace}
        />
      </div>

      <nav className="rr-rail-nav">
        {items.filter((i) => !i.hidden).map((item) => (
          <RailItem key={item.id} item={item} active={item.id === activeId} onNavigate={onNavigate} />
        ))}
      </nav>

      <div className="rr-rail-spacer" />

      {footerItems.length > 0 && (
        <nav className="rr-rail-nav rr-rail-nav--foot">
          {footerItems.filter((i) => !i.hidden).map((item) => (
            <RailItem key={item.id} item={item} active={item.id === activeId} onNavigate={onNavigate} />
          ))}
        </nav>
      )}
    </aside>
  );
}

function RailItem({
  item,
  active,
  onNavigate,
}: {
  item: NavItem;
  active: boolean;
  onNavigate?: (i: NavItem) => void;
}) {
  return (
    <button
      type="button"
      className={"rr-rail-item" + (active ? " is-active" : "")}
      aria-current={active ? "page" : undefined}
      onClick={() => onNavigate?.(item)}
    >
      <span className="rr-rail-item-icon">{renderIcon(item)}</span>
      <span className="rr-rail-tip">{item.label}</span>
      {item.count != null && <span className="rr-rail-item-dot" aria-hidden="true" />}
    </button>
  );
}

function renderIcon(item: NavItem): ReactNode {
  if (item.icon) return item.icon;
  if (item.iconPath) {
    return (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d={item.iconPath} />
      </svg>
    );
  }
  // Convenience: known ids map to our built-in icons.
  return (NavIcons as Record<string, ReactNode>)[item.id] ?? null;
}
